import React from 'react';
// The i18next SINGLETON the app configures — never the @pypestream/translations
// barrel (it pulls react-i18next). Same pattern as data-table.wc.ts.
import i18n from 'i18next';
// import { options } from 'marked';
import {
  toast,
  ToastTransition,
  Slide,
  Flip,
  ToastOptions,
  CloseButtonProps,
  ToastIcon,
} from 'react-toastify';
import { TextSubtitle, TextBody } from '../text';
import { Stack } from '../stack';
import { Spacer } from '../spacer';
import { IconButton } from '../buttons/icon-button/icon-button';
import { Icon } from '../icon';
import 'react-toastify/dist/ReactToastify.css';
import './toast-tweaks.scss';

export { toast, ToastContainer } from 'react-toastify';

export const dismissToasts = (id?: string): ReturnType<typeof toast.dismiss> =>
  toast.dismiss(id);

const CustomCloseButton = ({ closeToast }: CloseButtonProps) => (
  <IconButton
    name="close"
    size="xsmall"
    // Accessible name for the icon-only button (WCAG 4.1.2). Without it a screen
    // reader announces a nameless "button" — worse for a persistent toast
    // (autoClose: false), where this is the only way to dismiss it.
    label={
      i18n.t('designSystem/common:toast.close', { defaultValue: 'Close' }) ||
      'Close'
    }
    onClick={() => {
      // we are passing true to close toast, the closure reason is optional
      // you can specify any closure reason you want or simply omit it
      // by convention, true is used to tell that it was removed by a user action
      closeToast(true);
    }}
    className="c-toast__close-btn"
    test-id="toast-close-btn"
  />
);

const customIcon = (
  icon: ToastIcon | undefined,
  type: 'info' | 'warning' | 'error' | 'success'
) => {
  if (icon === false) {
    return false;
  }

  if (icon !== undefined) {
    return icon;
  }

  if (type === 'success') {
    return <Icon name="check" size="auto" className="c-toast__type-icon" />;
  }

  return <Icon name="warning" size="small" className="c-toast__type-icon" />;

  // this we'll be needed later when different icon will be added for toast types
  // switch (type) {
  //   case 'info':
  //     return <Icon name="info" size="small" className="c-toast__type-icon" />;
  //   case 'warning':
  //     return (
  //       <Icon name="warning" size="small" className="c-toast__type-icon" />
  //     );
  //   case 'error':
  //     return (
  //       <Icon name="exclamation" size="small" className="c-toast__type-icon" />
  //     );
  //   case 'success':
  //     return <Icon name="check" size="small" className="c-toast__type-icon" />;
  //   default:
  //     return <Icon name="info" size="small" className="c-toast__type-icon" />;
  // }
};

// https://stackoverflow.com/a/60617060
// note: the brackets are important here: "&" has precedence over "|"
// make sure that children OR text is passed in
export type ToastProps = {
  onClose?: () => void;
  autoClose?: number | false;
  type?: 'info' | 'warning' | 'error' | 'success';
  toastId?: string;
  header?: string;
  text: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  transition?: ToastTransition;
  position?:
    | 'top-right'
    | 'top-left'
    | 'top-center'
    | 'bottom-right'
    | 'bottom-left'
    | 'bottom-center';
};
// uncomment if more complex (nested) children is needed
// & (
//   | {
//       children: React.ReactNode;
//       text?: string;
//     }
//   | { children?: React.ReactNode; text: string }
// );

export function sendToast({
  header,
  text,
  children,
  action,
  footer,
  onClose,
  autoClose = false,
  type = 'info',
  toastId,
  transition = Slide,
  icon,
  ...opts
}: ToastProps & ToastOptions): {
  id: string;
  dismiss: () => void;
  checkIfActive: () => void;
} {
  // Logging to console can help loggers track errors / user actions
  const msg = `Pypestream Toast Message: ${text}`;

  /* eslint-disable no-console */
  switch (type) {
    case 'error':
      console.error(msg);
      break;
    case 'warning':
      console.warn(msg);
      break;
    case 'info':
    case 'success':
      console.log(msg);
      break;
    default:
      console.log('Default case');
      break;
  }
  /* eslint-enable no-console */

  const toastUuid = text?.toString() || 'toast-uuid';

  toast(
    <div className="c-toast__container">
      <div className="c-toast__content">
        {header && <TextSubtitle size="small">{header}</TextSubtitle>}
        <TextBody size="small">{text}</TextBody>
        {children}
        {footer && (
          <>
            <Spacer size="small" />
            <Stack gutter="xsmall" alignItems="center" justifyContent="end">
              {footer}
            </Stack>
          </>
        )}
      </div>
      {action && <div className="c-toast__action">{action}</div>}
    </div>,
    {
      autoClose,
      onClose,
      type,
      toastId: toastId || toastUuid,
      closeOnClick: false,
      transition,
      closeButton: CustomCloseButton,
      icon: customIcon(icon, type),
      ...opts,
    }
  );

  return {
    id: toastId || toastUuid,
    dismiss: () => toast.dismiss(toastUuid),
    checkIfActive: () => toast.isActive(toastUuid),
  };
}

export function updateToast({
  text,
  header,
  children,
  action,
  footer,
  toastId,
  transition = Flip,
  ...opts
}: ToastProps & {
  toastId: string;
} & ToastOptions): {
  id: string;
  dismiss: () => void;
  checkIfActive: () => void;
} {
  // Logging to console can help loggers track errors / user actions

  const toastUuid = text?.toString() || 'toast-uuid';

  toast.update(toastId || toastUuid, {
    render: () => (
      <div className="c-toast__container">
        <div className="c-toast__content">
          {header && <TextSubtitle size="small">{header}</TextSubtitle>}
          <TextBody size="small">{text}</TextBody>
          {children}
          {footer && (
            <>
              <Spacer size="small" />
              <Stack gutter="xsmall" alignItems="center" justifyContent="end">
                {footer}
              </Stack>
            </>
          )}
        </div>
        {action && <div className="c-toast__action">{action}</div>}
      </div>
    ),
    transition,
    toastId: toastId || toastUuid,
    closeOnClick: false,
    ...opts,
  });

  return {
    id: toastId || toastUuid,
    dismiss: () => toast.dismiss(toastUuid),
    checkIfActive: () => toast.isActive(toastUuid),
  };
}
